import { DetailsRow } from "~/components/details/DetailsSection"
import { ProjectStatusIcon } from "~/components/icons/project-status"
import { format } from "date-fns"
import { ArrowUpRightIcon, CalendarIcon, FolderKanbanIcon } from "lucide-react"
import { Link } from "~/components/Link"
import { Badge } from "~/components/ui/badge"
import { Progress } from "~/components/ui/progress"
import { enumFormat } from "~/lib/utils"
import { api } from "~/trpc/react"
import { IssueProjectPicker } from "./IssueProjectPicker"

export function IssueProjectPanel({
	teamId,
	projectId,
	orgSlug,
	teamSlug,
	onChange,
	disabled
}: {
	teamId: string
	projectId: string | null
	orgSlug: string
	teamSlug: string
	onChange: (projectId: string | null) => void
	disabled?: boolean
}) {
	const utils = api.useUtils()
	const projects = api.projects.list.useQuery({ teamId }, { enabled: !!teamId })
	api.issues.onUpdate.useSubscription(
		{ teamId },
		{
			enabled: !!teamId,
			onData: () => {
				void utils.projects.list.invalidate({ teamId })
			}
		}
	)
	const project = projects.data?.find((entry) => entry.id === projectId)
	return (
		<div className="flex min-w-0 flex-col gap-3">
			<DetailsRow label="Project">
				<IssueProjectPicker
					teamId={teamId}
					value={projectId}
					onChange={onChange}
					disabled={disabled}
				/>
			</DetailsRow>
			{project && (
				<>
					<Link
						to={`/${orgSlug}/${teamSlug}/projects/${project.id}`}
						className="inline-flex min-w-0 items-center gap-2 text-sm font-medium hover:underline"
					>
						<FolderKanbanIcon className="size-4 shrink-0 text-icons" />
						<span className="min-w-0 break-words">{project.name}</span>
						<ArrowUpRightIcon className="ml-auto size-4 shrink-0 text-icons" />
					</Link>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<Badge
							variant="secondary"
							aria-label={`Project status: ${enumFormat(project.status)}`}
						>
							<ProjectStatusIcon status={project.status} withText />
						</Badge>
						<span className="text-xs text-text-sub">
							{project.completed}/{project.total} complete
						</span>
					</div>
					<Progress
						aria-label={`${project.name} completion, excluding canceled issues`}
						value={
							project.total ? (project.completed / project.total) * 100 : 0
						}
					/>
					{project.targetDate && (
						<span className="flex items-center gap-2 text-xs text-text-sub">
							<CalendarIcon className="size-3.5 shrink-0" />
							Target {format(project.targetDate, "MMM d, yyyy")}
						</span>
					)}
				</>
			)}
		</div>
	)
}
